/**
 * Audience card labels: a deterministic one-or-two-word read on HOW a
 * canonical audience segment was built, derived at read time from the
 * segment's resolved query (Pulse topics + VisitIQ person filters). Nothing is
 * saved and the agent never writes a label, so it can never go stale or be
 * invented.
 *
 * Vocabulary (product decision, 2026-09-10):
 * - 'intent'      — the segment carries Pulse topics. Shown whenever present.
 * - 'b2b'         — either leg says business: a firmographic person filter
 *                   (Department, JobTitle, Company*, …) OR a topic under a B2B
 *                   taxonomy branch. Both legs matter: VisitIQ sometimes
 *                   returns the occupation filter empty (seen live on the
 *                   ad6abc03 "HR & Team-Building" card), leaving the
 *                   human-resources topics as the only B2B evidence.
 * - 'demographic' — the umbrella for every consumer profile filter (age,
 *                   income, homeownership, interests, …), the way reps say
 *                   "demo".
 *
 * At most two labels, ordered [who, intent]: the profile word first, then
 * 'intent' as the overlay ("B2B + Intent"). When a card would earn all three,
 * 'b2b' takes the profile slot over 'demographic'.
 *
 * Deliberately NOT labels:
 * - Geography. Every segment inherits the proposal radius, so it defines
 *   nothing about the card (and the geo leg is never saved on the segment).
 * - The plain-English personFilterPrompt. Labels read only the RESOLVED
 *   filters, so an unsized or failed profile segment carries no profile label
 *   until VisitIQ has actually turned its prompt into filters.
 *
 * Returned as an array so the UI composes each label separately.
 */

import type { AudienceSegment, PersonFilterInput } from './types.js';

export type AudienceLabel = 'intent' | 'b2b' | 'demographic';

/** Display text per label; the UI owns casing/styling beyond this. */
export const AUDIENCE_LABEL_TEXT: Readonly<Record<AudienceLabel, string>> = {
  intent: 'Intent',
  b2b: 'B2B',
  demographic: 'Demographic',
};

/**
 * VisitIQ person-filter fields that describe the person's job or company
 * (GraphQL `FilterField` enum). Anything else with a field name is a consumer
 * profile attribute.
 */
export const B2B_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'JobTitle',
  'Department',
  'SeniorityLevel',
  'PrimaryIndustry',
  'OccupationDetail',
  'BusinessEmail',
  'CompanyName',
  'CompanyDomain',
  'CompanyCity',
  'CompanyState',
  'CompanyRevenue',
  'CompanyEmployeeCount',
  'CompanySic',
]);

/**
 * Top-level Pulse taxonomy branches (first pipe-delimited segment of a topic
 * id) whose topics are business buying signals. NOT 'business-services': that
 * branch is consumer home services (roofing, plumbing, cleaning) and must stay
 * consumer. 'uncategorized' says nothing either way.
 */
export const B2B_TOPIC_BRANCHES: ReadonlySet<string> = new Set([
  'business',
  'human-resources',
  'events-conferences',
  'bio-tech',
]);

/** PersonFilterInput keys whose value carries a `field`; geoFilter has none. */
const LEAF_FILTER_KEYS = [
  'stringFilter',
  'numberFilter',
  'booleanFilter',
  'dateFilter',
  'extendedStringFilter',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Every named field in a filter list, descending into compoundFilter. Empty
 * objects (an all-null filter after stripNulls) and geo legs contribute
 * nothing. Order follows the input; duplicates are kept.
 */
export function filterFields(filters: readonly PersonFilterInput[] | undefined): string[] {
  const fields: string[] = [];
  const visit = (filter: unknown, depth: number): void => {
    if (depth > 6 || !isRecord(filter)) return;
    for (const key of LEAF_FILTER_KEYS) {
      const leaf = filter[key];
      if (isRecord(leaf) && typeof leaf['field'] === 'string' && leaf['field'].trim()) {
        fields.push(leaf['field']);
      }
    }
    const compound = filter['compoundFilter'];
    if (isRecord(compound) && Array.isArray(compound['filters'])) {
      for (const nested of compound['filters']) visit(nested, depth + 1);
    }
  };
  for (const filter of filters ?? []) visit(filter, 0);
  return fields;
}

/** First pipe-delimited segment of a Pulse topic id, lower-cased; '' when absent. */
export function topicBranch(topicId: string): string {
  return topicId.split('|')[0]?.trim().toLowerCase() ?? '';
}

/**
 * The labels for one segment, per the rules in the file header. Pure: reads
 * only `topics` and `personFilters`.
 */
export function classifyAudienceSegment(
  segment: Pick<AudienceSegment, 'topics' | 'personFilters'>,
): AudienceLabel[] {
  const topicIds = (segment.topics ?? [])
    .map((topic) => topic.id?.trim() ?? '')
    .filter((id) => id.length > 0);
  const fields = filterFields(segment.personFilters);

  const hasIntent = topicIds.length > 0;
  const b2bTopic = topicIds.some((id) => B2B_TOPIC_BRANCHES.has(topicBranch(id)));
  const b2bField = fields.some((field) => B2B_FILTER_FIELDS.has(field));
  const consumerField = fields.some((field) => !B2B_FILTER_FIELDS.has(field));

  const b2b = b2bTopic || b2bField;
  const labels: AudienceLabel[] = [];
  if (b2b) labels.push('b2b');
  // 'demographic' fills the second slot only when 'b2b' + 'intent' haven't
  // already claimed both — never more than two labels.
  if (consumerField && !(b2b && hasIntent)) labels.push('demographic');
  if (hasIntent) labels.push('intent');
  return labels;
}
